import React, { useEffect } from "react";
import { SITE } from "@/data/site";
import { IMAGES } from "@/data/images";

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Extreme Teambuilding",
  alternateName: "Extreme Teambuilding by Warehouse Liberec",
  url: SITE.url,
  email: SITE.email,
  telephone: SITE.phone,
  parentOrganization: {
    "@type": "Organization",
    name: SITE.company,
    identifier: SITE.ico,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Hanychovská 328/10",
      addressLocality: "Liberec",
      postalCode: "460 07",
      addressCountry: "CZ",
    },
  },
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Extreme Teambuilding",
  url: SITE.url,
};

const serviceSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: page.h1,
  description: page.seo.description,
  url: `${SITE.url}${page.path}`,
  provider: ORG_SCHEMA,
  areaServed: { "@type": "Country", name: "Czechia" },
  serviceType: "Firemní teambuilding",
});

const webPageSchema = (page) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: page.seo.title,
  description: page.seo.description,
  url: `${SITE.url}${page.path}`,
  isPartOf: { "@type": "WebSite", name: "Extreme Teambuilding", url: SITE.url },
});

const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.label,
    item: `${SITE.url}${c.href}`,
  })),
});

const articleSchema = (guide) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: guide.h1,
  description: guide.description,
  author: { "@type": "Organization", name: guide.author, url: SITE.url },
  publisher: ORG_SCHEMA,
  datePublished: guide.publishedAt,
  dateModified: guide.updatedAt,
  mainEntityOfPage: `${SITE.url}${guide.path}`,
});

// Nativní React 19 hoisting: <title>/<meta>/<link> se v prohlížeči přesunou do <head>.
// Při SSG (scripts/prerender.js) se tagy s data-head="1" z markupu extrahují a vloží do <head> ručně.
export const Seo = ({ page, guide }) => {
  const canonical = `${SITE.url}${page.path}`;
  const robots = page.seo.robots || "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
  const ogImage = IMAGES.heroArrival.src;
  const lang = page.seo.lang || "cs";

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const schemas = [];
  const types = page.seo.schema || ["WebPage"];
  if (types.includes("Organization")) schemas.push(ORG_SCHEMA);
  if (types.includes("WebSite")) schemas.push(WEBSITE_SCHEMA);
  if (types.includes("Service")) schemas.push(serviceSchema(page));
  if (types.includes("WebPage")) schemas.push(webPageSchema(page));
  if (types.includes("Article") && guide) schemas.push(articleSchema(guide));
  if (page.breadcrumbs && page.breadcrumbs.length > 1 && types.includes("BreadcrumbList")) {
    schemas.push(breadcrumbSchema(page.breadcrumbs));
  }
  // FAQPage — jen když je FAQ viditelné na stránce (shoda markupu s obsahem)
  if (robots.indexOf("noindex") === -1) {
    const faqBlock = (page.blocks || []).find((b) => b.t === "faq" && b.items?.length);
    if (faqBlock) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqBlock.items.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }
    const heroImgKey = (page.blocks || []).find((b) => b.t === "hero" && b.img)?.img;
    const heroImg = heroImgKey ? IMAGES[heroImgKey] : null;
    if (heroImg) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "ImageObject",
        contentUrl: heroImg.src,
        name: heroImg.alt,
        width: heroImg.w,
        height: heroImg.h,
      });
    }
  }

  return (
    <>
      <title data-head="1">{page.seo.title}</title>
      <meta data-head="1" name="description" content={page.seo.description} />
      <link data-head="1" rel="canonical" href={canonical} />
      <meta data-head="1" name="robots" content={robots} />
      <meta data-head="1" property="og:type" content={guide ? "article" : "website"} />
      <meta data-head="1" property="og:site_name" content="Extreme Teambuilding" />
      <meta data-head="1" property="og:title" content={page.seo.title} />
      <meta data-head="1" property="og:description" content={page.seo.description} />
      <meta data-head="1" property="og:url" content={canonical} />
      <meta data-head="1" property="og:image" content={ogImage} />
      <meta data-head="1" property="og:locale" content={lang === "en" ? "en_US" : "cs_CZ"} />
      <meta data-head="1" name="twitter:card" content="summary_large_image" />
      <meta data-head="1" name="twitter:title" content={page.seo.title} />
      <meta data-head="1" name="twitter:description" content={page.seo.description} />
      <meta data-head="1" name="twitter:image" content={ogImage} />
      <link data-head="1" rel="alternate" hreflang={lang} href={canonical} />
      <link data-head="1" rel="alternate" hreflang="x-default" href={canonical} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </>
  );
};
